/**
 * useAvailableDates — единый источник списка доступных дат NDVI/GNR snapshots.
 *
 * Backend endpoint: GET /api/v1/ndvi/available-dates → { dates: string[] (ISO YYYY-MM-DD) }.
 *
 * AK-71: эндпоинт принимает scope params (farm_id|cluster_id|brigade_id) —
 * возвращает только те даты, где есть scenes покрывающие хотя бы одно поле scope.
 * Когда выбрано хозяйство «Янкульская», SatellitesPanel показывает только
 * актуальные для него снимки (а не глобальный список).
 *
 * AK-77: добавлен параметр `indexCode` ('ndvi' | 'gnr') — список дат GNR может
 * отличаться от NDVI (pipeline иногда не генерирует GNR.cog для старых сцен).
 * Если фронт показывал NDVI-даты в GNR-режиме, selectedDate указывала на дату
 * без GNR cog → /scene-gnr возвращал пустой PNG и founder видел пустую карту.
 *
 * Используется:
 *   - MapCanvas: для auto-correct selectedDate при смене даты/режима.
 *   - SatellitesPanel: для отображения row-2 дат (зависит от текущего mode).
 *   - CompareDatesView: для compare mode.
 */
import { useEffect, useState } from 'react';
import type { ActiveScope } from './useMapStore';

interface AvailableDatesResponse {
  dates?: string[];
}

export type AvailableDatesIndexCode = 'ndvi' | 'gnr';

/** Cache + inflight per-(indexCode, scope) — стабильная сериализация ключа.
 *
 * Раньше был один module-level cache → при смене scope залипал старый список.
 * Теперь ключ включает indexCode + scope, кеш прогревается отдельно под
 * каждую комбинацию.
 */
const cacheByKey = new Map<string, string[]>();
const inflightByKey = new Map<string, Promise<string[] | null>>();

function makeKey(indexCode: AvailableDatesIndexCode, scope?: ActiveScope | null): string {
  const sk =
    !scope || !scope.id || scope.type === 'company'
      ? '__company__'
      : `${scope.type}:${scope.id}`;
  return `${indexCode}::${sk}`;
}

function buildUrl(indexCode: AvailableDatesIndexCode, scope?: ActiveScope | null): string {
  const params = new URLSearchParams();
  params.set('index_code', indexCode);
  if (scope && scope.id) {
    if (scope.type === 'farm') params.set('farm_id', scope.id);
    else if (scope.type === 'cluster') params.set('cluster_id', scope.id);
    // brigade пока не используется в frontend ActiveScope, но запас оставляем
    else if (scope.type === ('brigade' as unknown as ActiveScope['type']))
      params.set('brigade_id', scope.id);
  }
  return `/api/v1/ndvi/available-dates?${params.toString()}`;
}

async function fetchOnce(
  indexCode: AvailableDatesIndexCode,
  scope?: ActiveScope | null,
): Promise<string[] | null> {
  const key = makeKey(indexCode, scope);
  const cached = cacheByKey.get(key);
  if (cached) return cached;
  const inflight = inflightByKey.get(key);
  if (inflight) return inflight;

  const promise = (async () => {
    try {
      const r = await fetch(buildUrl(indexCode, scope), { credentials: 'include' });
      if (!r.ok) return null;
      const data = (await r.json()) as AvailableDatesResponse;
      const dates = (data?.dates ?? []).filter(Boolean).sort(); // ISO sort
      cacheByKey.set(key, dates);
      return dates;
    } catch {
      return null;
    } finally {
      inflightByKey.delete(key);
    }
  })();
  inflightByKey.set(key, promise);
  return promise;
}

interface UseAvailableDatesOptions {
  indexCode?: AvailableDatesIndexCode;
  scope?: ActiveScope | null;
}

/** Backwards-compatible: можно вызвать `useAvailableDates(scope)` (legacy)
 * либо `useAvailableDates({ indexCode, scope })` (AK-77).
 */
export function useAvailableDates(
  arg?: ActiveScope | null | UseAvailableDatesOptions,
): string[] | null {
  const isOptions =
    arg !== null && typeof arg === 'object' && 'indexCode' in (arg as object);
  const indexCode: AvailableDatesIndexCode = isOptions
    ? ((arg as UseAvailableDatesOptions).indexCode ?? 'ndvi')
    : 'ndvi';
  const scope: ActiveScope | null | undefined = isOptions
    ? (arg as UseAvailableDatesOptions).scope
    : (arg as ActiveScope | null | undefined);

  const key = makeKey(indexCode, scope);
  const [dates, setDates] = useState<string[] | null>(() => cacheByKey.get(key) ?? null);

  useEffect(() => {
    let cancelled = false;
    // При смене scope/indexCode сразу показываем cached (если есть) — иначе null,
    // потом дотягиваем актуальный список.
    setDates(cacheByKey.get(key) ?? null);
    void fetchOnce(indexCode, scope).then((d) => {
      if (!cancelled && d) setDates(d);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return dates;
}
